import { useState, useEffect } from 'react';
import { Brain, Activity, Play, Square } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/common/Card';
import Input from '@/components/common/Input';
import Select from '@/components/common/Select';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useSystemStatus, useStartAgent, useStopAgent } from '@/hooks/useSystem';
import { useConfig, useConfigMutate } from '@/hooks/useConfig';

const AI_PROVIDERS = [
  { value: 'none', label: 'None', description: 'Disable AI features' },
  { value: 'groq', label: 'Groq (Recommended - Free)', description: 'Fast, free tier available' },
  { value: 'openai', label: 'OpenAI', description: 'GPT models, paid' },
  { value: 'anthropic', label: 'Anthropic', description: 'Claude models, paid' },
];

const AI_MODELS = {
  groq: [
    { value: 'llama-3.3-70b-versatile', label: 'Llama 3.3 70B (Recommended)' },
    { value: 'llama-3.1-70b-versatile', label: 'Llama 3.1 70B' },
    { value: 'mixtral-8x7b-32768', label: 'Mixtral 8x7B' },
  ],
  openai: [
    { value: 'gpt-4-turbo-preview', label: 'GPT-4 Turbo' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
  ],
  anthropic: [
    { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
    { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
  ],
};

function AiConfigSection() {
  const { data: config, isLoading } = useConfig();
  const updateConfig = useConfigMutate();

  const [formData, setFormData] = useState({
    ai_provider: 'none', ai_model: '', ai_api_key: '',
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (config) {
      setFormData({
        ai_provider: config.ai_provider || 'none',
        ai_model: config.ai_model || '',
        ai_api_key: '',
      });
    }
  }, [config]);

  const handleProviderChange = (provider) => {
    const models = AI_MODELS[provider];
    setFormData(prev => ({
      ...prev,
      ai_provider: provider,
      ai_model: models ? models[0].value : '',
    }));
  };

  const handleSave = async () => {
    try {
      const payload = formData.ai_provider === 'none'
        ? { ai_provider: 'none' }
        : { ai_provider: formData.ai_provider, ai_model: formData.ai_model, ai_api_key: formData.ai_api_key };
      await updateConfig.mutateAsync(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save AI config:', err);
    }
  };

  const isAiConfigured = formData.ai_provider !== 'none';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Brain className="h-5 w-5" />AI Configuration</CardTitle>
        <CardDescription>Configure the AI provider for email analysis and categorization</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? <LoadingSpinner /> : (
          <div className="space-y-4">
            <div className="grid gap-3">
              {AI_PROVIDERS.map(provider => (
                <div key={provider.value} onClick={() => handleProviderChange(provider.value)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${formData.ai_provider === provider.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{provider.label}</h3>
                        {provider.value === 'groq' && <Badge variant="success" className="text-xs">Recommended</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{provider.description}</p>
                    </div>
                    <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${formData.ai_provider === provider.value ? 'border-primary' : 'border-muted'}`}>
                      {formData.ai_provider === provider.value && <div className="h-3 w-3 rounded-full bg-primary" />}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {isAiConfigured && (
              <Select label="AI Model" value={formData.ai_model} onChange={(e) => setFormData(prev => ({ ...prev, ai_model: e.target.value }))}>
                {AI_MODELS[formData.ai_provider].map(model => (
                  <option key={model.value} value={model.value}>{model.label}</option>
                ))}
              </Select>
            )}

            {isAiConfigured && (
              <Input label="API Key" type="password" placeholder={`Enter your ${formData.ai_provider} API key`}
                value={formData.ai_api_key} onChange={(e) => setFormData(prev => ({ ...prev, ai_api_key: e.target.value }))} />
            )}

            {!isAiConfigured && (
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900">
                <p className="text-sm text-amber-900 dark:text-amber-100">AI features are disabled. Enable them above to use AI-powered email categorization and analysis.</p>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button onClick={handleSave} disabled={updateConfig.isPending} loading={updateConfig.isPending}>Save</Button>
              {saved && <span className="text-sm text-green-600">Configuration saved!</span>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AgentControl() {
  const { data: status, isLoading } = useSystemStatus();
  const startMutation = useStartAgent();
  const stopMutation = useStopAgent();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Activity className="h-5 w-5" />Agent Control</CardTitle>
        <CardDescription>Start or stop the email processing agent</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? <LoadingSpinner /> : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`h-3 w-3 rounded-full ${status?.isRunning ? 'bg-green-500' : 'bg-gray-400'}`} />
                <div>
                  <p className="font-medium">Agent Status</p>
                  <p className="text-sm text-muted-foreground">{status?.isRunning ? 'Running' : 'Stopped'}</p>
                </div>
              </div>
              <Badge variant={status?.isRunning ? 'success' : 'secondary'}>{status?.isRunning ? 'Active' : 'Inactive'}</Badge>
            </div>
            {status?.uptime && status.isRunning && (
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground">Uptime</p>
                <p className="text-lg font-semibold">{Math.floor(status.uptime / 3600)}h {Math.floor((status.uptime % 3600) / 60)}m</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={() => startMutation.mutate()} disabled={status?.isRunning} loading={startMutation.isPending}
                className="flex items-center gap-2"><Play className="h-4 w-4" /> Start Agent</Button>
              <Button onClick={() => stopMutation.mutate()} disabled={!status?.isRunning} loading={stopMutation.isPending}
                variant="destructive" className="flex items-center gap-2"><Square className="h-4 w-4" /> Stop Agent</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function SettingsAI() {
  return (
    <div className="space-y-6">
      <AgentControl />
      <AiConfigSection />
    </div>
  );
}
